/**
 * Windows Retro — classic Microsoft Access 97 / Windows 95 styling.
 * Square corners, beveled borders, Tahoma. For accountants who
 * worked decades on Hashavshevet's native client and feel at home
 * with the retro chrome.
 */
import { createTheme } from '@mui/material/styles';

const W = {
  primary: '#000080',
  bg: '#C0C0C0',
  surface: '#ECECEC',
  surfaceAlt: '#D4D0C8',
  border: '#808080',
  borderLight: '#A0A0A0',
  text: '#000000',
  textSecondary: '#404040',
};

const sharedWin = {
  typography: {
    fontFamily: '"Tahoma", "MS Sans Serif", "Rubik", system-ui, sans-serif',
    h1: { fontWeight: 700, letterSpacing: 0 },
    h2: { fontWeight: 700, letterSpacing: 0 },
    h3: { fontWeight: 700, letterSpacing: 0 },
    h4: { fontWeight: 700, letterSpacing: 0 },
    h5: { fontWeight: 700, letterSpacing: 0, fontSize: '1.1rem' },
    h6: { fontWeight: 700, fontSize: '0.95rem' },
    body1: { fontSize: '0.85rem', lineHeight: 1.5 },
    body2: { fontSize: '0.78rem', lineHeight: 1.5 },
    button: { fontSize: '0.8rem', textTransform: 'none' as const },
    caption: { fontSize: '0.7rem' },
  },
  palette: {
    primary: { main: W.primary },
    background: { default: W.bg, paper: W.surface },
    text: { primary: W.text, secondary: W.textSecondary },
    divider: W.border,
  },
  shape: { borderRadius: 0 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: W.bg,
          fontFamily: '"Tahoma","MS Sans Serif",sans-serif',
        },
        '*::-webkit-scrollbar': { width: 16, height: 16 },
        '*::-webkit-scrollbar-thumb': { backgroundColor: '#A0A0A0', border: '1px solid #808080' },
        '*::-webkit-scrollbar-track': { backgroundColor: '#D4D0C8' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, variant: 'outlined' as const },
      styleOverrides: {
        root: {
          borderRadius: 0,
          padding: '4px 16px',
          fontSize: '0.8rem',
          fontWeight: 400,
          backgroundColor: W.surface,
          border: `1px solid ${W.border}`,
          // beveled look — top/left lighter, bottom/right darker
          borderTopColor: '#FFFFFF',
          borderLeftColor: '#FFFFFF',
          borderBottomColor: W.border,
          borderRightColor: W.border,
          color: W.text,
          '&:hover': { backgroundColor: '#D8D4CC' },
          '&:active': {
            borderTopColor: W.border,
            borderLeftColor: W.border,
            borderBottomColor: '#FFFFFF',
            borderRightColor: '#FFFFFF',
          },
        },
        contained: {
          background: W.surface, color: W.text,
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' as const },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 0, fontSize: '0.8rem', backgroundColor: '#FFFFFF',
            '& fieldset': { borderColor: W.border },
            '&:hover fieldset': { borderColor: '#000000' },
          },
          '& input': { padding: '6px 10px' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: `1px solid ${W.border}`,
          backgroundColor: W.surface,
          boxShadow: 'inset 1px 1px 0 #FFFFFF, inset -1px -1px 0 #808080',
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { borderRadius: 0, backgroundImage: 'none' } },
    },
    MuiAppBar: {
      defaultProps: { color: 'inherit' as const, elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: W.surface, color: W.text,
          borderBottom: `1px solid ${W.border}`,
          boxShadow: 'inset 0 -1px 0 #FFFFFF',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            fontWeight: 700, fontSize: '0.78rem',
            color: W.text, backgroundColor: W.surfaceAlt,
            borderBottom: `1px solid ${W.border}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { fontSize: '0.8rem', borderColor: W.borderLight, padding: '6px 10px' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 0, fontSize: '0.7rem', height: 22 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.7rem', backgroundColor: '#FFFFE1', color: '#000',
          border: '1px solid #000', borderRadius: 0,
        },
      },
    },
  },
};

export const winRetroRtlTheme = createTheme({ ...sharedWin, direction: 'rtl' });
export const winRetroLtrTheme = createTheme({ ...sharedWin, direction: 'ltr' });

export const WIN_RETRO_SIDEBAR_COLORS = {
  background:      W.surface,
  backgroundLight: W.surfaceAlt,
  text:            W.text,
  textSecondary:   W.textSecondary,
  activeItem:      '#316AC5',
  hoverItem:       '#D8D4CC',
  activeText:      '#FFFFFF',
  border:          W.border,
};
